import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Palette, Save, Upload, Trash2 } from "lucide-react";
import { useBrand } from "@/context/BrandContext";

const API = process.env.REACT_APP_BACKEND_URL;
const FIELDS = [
  ["product_name", "Product name", "SteelForgeAI", "Shown in the sidebar, browser tab, emails and report headers"],
  ["company_name", "Company / legal name", "e.g. SteelForge Technologies LLC", "Used on legal footers and invoices later"],
  ["tagline", "Tagline", "Document Intelligence. Structural Confidence.", "Under the logo and on report covers"],
  ["support_email", "Support email", "support@yourdomain.com", "Where users are told to write for help"],
  ["website_url", "Website URL", "https://yourdomain.com", "Linked from emails and the login page"],
  ["report_footer", "Report footer", "Confidential — prepared for bidding purposes only", "Replaces the default footer line on every generated PDF"],
];
const inputCls = "mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-amber-500";
const fail = (e) => toast.error(e?.response?.data?.detail || "Failed");

function AssetCard({ kind, label, hint, url, onChanged }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/platform/branding/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${label} updated`); onChanged(); }
    catch (e) { fail(e); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const remove = async () => { try { await api.delete(`/platform/branding/${kind}`); toast.success(`${label} removed`); onChanged(); } catch (e) { fail(e); } };
  return (
    <div className="sc-card p-4" data-testid={`branding-${kind}-card`}>
      <div className="label-cap mb-2">{label}</div>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-md bg-slate-950/70 border border-slate-800 flex items-center justify-center overflow-hidden">
          {url ? <img src={`${API}${url}`} alt={label} className="max-w-full max-h-full object-contain" data-testid={`branding-${kind}-preview`} /> : <span className="text-[10px] text-slate-600 font-mono">default</span>}
        </div>
        <div className="flex-1 text-xs text-slate-400">{hint}
          <div className="mt-2 flex gap-2">
            <button disabled={busy} onClick={() => ref.current?.click()} className="border border-slate-700 hover:border-amber-500 text-slate-200 px-3 py-1.5 rounded text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid={`branding-${kind}-upload-button`}><Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload"}</button>
            {url && <button onClick={remove} className="text-slate-500 hover:text-red-400 px-2 inline-flex items-center gap-1 text-xs" data-testid={`branding-${kind}-remove-button`}><Trash2 className="w-3.5 h-3.5" /> Remove</button>}
          </div>
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`branding-${kind}-file-input`} />
        </div>
      </div>
    </div>
  );
}

export default function PlatformBranding() {
  const { brand, refresh } = useBrand();
  const [form, setForm] = useState(brand);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(brand), [brand]);
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(FIELDS.map(([k]) => [k, form[k] || ""]));
      await api.put("/platform/branding", body);
      toast.success("Branding saved"); refresh();
    } catch (e) { fail(e); } finally { setSaving(false); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="platform-branding">
      <div className="sc-card p-5 lg:col-span-2">
        <div className="label-cap mb-3 flex items-center gap-1.5"><Palette className="w-3.5 h-3.5 text-amber-400" /> Product identity</div>
        <p className="text-xs text-slate-500 mb-4">One place for the name and contact details used across the app, emails and generated reports. Partner white-label branding still overrides these for partner tenants.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map(([k, label, ph, help]) => (
            <label key={k} className={`block ${k === "report_footer" ? "md:col-span-2" : ""}`}>
              <span className="label-cap">{label}</span>
              <input value={form[k] || ""} placeholder={ph} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls} data-testid={`branding-${k}-input`} />
              <span className="text-[11px] text-slate-500">{help}</span>
            </label>
          ))}
        </div>
        <button disabled={saving} onClick={save} className="mt-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="branding-save-button"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save branding"}</button>
      </div>
      <div className="space-y-4">
        <AssetCard kind="logo" label="Logo" hint="PNG, JPEG, WebP or SVG, under 2 MB. Used in the sidebar, login page and report covers." url={brand.logo_url} onChanged={refresh} />
        <AssetCard kind="favicon" label="Favicon" hint="PNG or ICO, under 512 KB. Shown in the browser tab." url={brand.favicon_url} onChanged={refresh} />
      </div>
    </div>
  );
}
